import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { BulkCustomerUploadResponse, Company, Role } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { errorMessage } from '../../utils/errorMessage';
import { formatBulkUploadError } from '../../utils/bulkUploadError';

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkCustomerUploadResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  // Load companies for super admin
  useEffect(() => {
    const loadCompanies = async () => {
      if (isSuperAdmin && open) {
        try {
          setLoadingCompanies(true);
          const companiesData = await apiClient.getCompanies();
          setCompanies(companiesData);
        } catch (err) {
          setError(errorMessage(err, t('bulkUpload.failedToLoadCompanies')));
        } finally {
          setLoadingCompanies(false);
        }
      }
    };

    loadCompanies();
  }, [isSuperAdmin, open, t]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        setError(t('bulkUpload.pleaseSelectCsvFile'));
        return;
      }
      setSelectedFile(file);
      setError('');
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t('bulkUpload.pleaseSelectFile'));
      return;
    }

    if (isSuperAdmin && !selectedCompanyId) {
      setError(t('bulkUpload.pleaseSelectCompany'));
      return;
    }

    try {
      setIsUploading(true);
      setError('');
      
      const result = await apiClient.bulkUploadCustomers(selectedFile, selectedCompanyId || undefined);
      setUploadResult(result);
      
      if (result.successCount > 0) {
        onSuccess();
      }
    } catch (err: unknown) {
      setError(formatBulkUploadError(errorMessage(err, t('bulkUpload.uploadFailed')), t));
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError('');
    setSelectedCompanyId('');
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = 'firstName,lastName,email,phone,address,taxIdentificationNumber\nJohn,Doe,john.doe@example.com,+1234567890,123 Main St,1234567890\nJane,Smith,jane.smith@example.com,,456 Oak Ave,\nBob,Johnson,,,789 Pine Rd,\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'customers_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <AppDialog presentation="compact" open={open} onClose={handleClose} closeDisabled={isUploading} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t('bulkUploadCustomers.bulkUploadCustomers')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('bulkUploadCustomers.uploadDescription')}
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            sx={{ mt: 1 }}
          >
            {t('bulkUpload.downloadTemplate')}
          </Button>
        </Box>

        {isSuperAdmin && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="company-select-label">{t('bulkUpload.selectCompany')}</InputLabel>
              <Select
                labelId="company-select-label"
                value={selectedCompanyId}
                label={t('bulkUpload.selectCompany')}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={loadingCompanies}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-file-input"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="csv-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{ py: 2 }}
            >
              {selectedFile ? selectedFile.name : t('bulkUpload.selectCsvFile')}
            </Button>
          </label>
        </Box>

        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              {t('bulkUploadCustomers.uploadingCustomers')}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {uploadResult && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('bulkUpload.uploadResults')}
            </Typography>
            
            <Box display="flex" gap={2} mb={2}>
              <Chip 
                label={`${t('bulkUpload.total')}: ${uploadResult.totalProcessed}`} 
                color="default" 
                size="small" 
              />
              <Chip 
                label={`${t('bulkUpload.success')}: ${uploadResult.successCount}`} 
                color="success" 
                size="small" 
              />
              <Chip 
                label={`${t('bulkUpload.errors')}: ${uploadResult.errorCount}`} 
                color="error" 
                size="small" 
              />
            </Box>

            {uploadResult.successfulCustomers.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  {t('bulkUploadCustomers.successfullyCreatedCustomers')}:
                </Typography>
                <List dense>
                  {uploadResult.successfulCustomers.slice(0, 5).map((customer, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={customer} />
                    </ListItem>
                  ))}
                  {uploadResult.successfulCustomers.length > 5 && (
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`... ${t('bulkUpload.andMore').replace('{{count}}', (uploadResult.successfulCustomers.length - 5).toString())}`}
                        sx={{ fontStyle: 'italic' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {uploadResult.errors.length > 0 && (
              <Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="error.main" gutterBottom>
                  {t('bulkUpload.errors')}:
                </Typography>
                <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
                  {uploadResult.errors.map((error, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={formatBulkUploadError(error, t)}
                        primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {uploadResult ? t('common.close') : t('common.cancel')}
        </Button>
        {!uploadResult && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading || (isSuperAdmin && !selectedCompanyId)}
          >
            {t('bulkUpload.uploadCustomers')}
          </Button>
        )}
      </DialogActions>
    </AppDialog>
  );
};

export default BulkUploadDialog;
